import { Navigate, Route, Routes } from 'react-router-dom'
import { Suspense, lazy, JSX } from 'react'
import { useUser } from '@/store'
import { links } from './links'
import { Spinner } from '@heroui/react'
import { useAuth } from '../hooks/useAuth'
import { useOperatorAuth } from '../hooks/useOperatorAuth'

const Connect = lazy(() => import('./connect').then((module) => ({ default: module.Connect })))
const Dashboard = lazy(() =>
  import('./dashboard').then((module) => ({ default: module.Dashboard })),
)
const Referrals = lazy(() =>
  import('./referrals').then((module) => ({ default: module.Referrals })),
)
const Operator = lazy(() => import('./operator').then((module) => ({ default: module.Operator })))

const RouterGuard = ({ children }: { children: JSX.Element }) => {
  const isAuth = useUser((state) => state.isAuth)
  const { privyAuthenticated, hasAcceptedTerms } = useAuth()

  // Redirect if not authenticated
  if (!isAuth) {
    return <Navigate to={links.connect.root} />
  }

  // Check if user has accepted terms - if authenticated but terms not accepted, redirect to connect
  if (isAuth && privyAuthenticated && !hasAcceptedTerms) {
    console.log('User has not accepted terms, redirecting to connect')
    return <Navigate to={links.connect.root} />
  }

  return children
}

const AuthGuard = ({ children }: { children: JSX.Element }) => {
  const isAuth = useUser((state) => state.isAuth)
  const { hasAcceptedTerms } = useAuth()

  // Only redirect to dashboard if authenticated AND terms are accepted
  if (isAuth && hasAcceptedTerms) {
    return <Navigate to={links.dashboard.root} />
  }
  
  // If authenticated but terms not accepted, stay on connect page
  return children
}

const OperatorGuard = ({ children }: { children: JSX.Element }) => {
  const isAuth = useUser((state) => state.isAuth)
  const { isOperator, loading } = useOperatorAuth()

  // Must be authenticated to proceed
  if (!isAuth) {
    return <Navigate to={links.connect.root} replace />
  }

  // Show loading spinner while checking operator status
  if (loading) {
    return (
      <div className='w-full h-screen flex items-center justify-center'>
        <Spinner className='w-8 h-8' />
      </div>
    )
  }

  // Only allow access if explicitly verified as operator (true)
  // Any other value (false) redirects to dashboard
  if (!isOperator) {
    return <Navigate to={links.dashboard.root} replace />
  }

  return children
}

export const MainRouter = () => {
  // Run auth validation on every page
  useAuth()

  return (
    <Suspense
      fallback={
        <div className='w-full h-screen flex items-center justify-center'>
          <Spinner className='w-8 h-8' />
        </div>
      }
    >
      <Routes>
        <Route
          path={links.connect.root}
          element={
            <AuthGuard>
              <Connect />
            </AuthGuard>
          }
        />
        <Route
          path={links.dashboard.root}
          element={
            <RouterGuard>
              <Dashboard />
            </RouterGuard>
          }
        />
        <Route
          path={links.referrals}
          element={
            <RouterGuard>
              <Referrals />
            </RouterGuard>
          }
        />
        <Route
          path={links.operator.root}
          element={
            <OperatorGuard>
              <Operator />
            </OperatorGuard>
          }
        />
        <Route path={'*'} element={<Navigate to={links.connect.root} />} />
      </Routes>
    </Suspense>
  )
}
